import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { medicamentoService } from '../services';
import useDebounce from '../hooks/useDebounce';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  AlertTriangle,
  Package,
  Calendar,
  Filter,
  List,
  Eye,
  Layers
} from 'lucide-react';

const Medicamentos = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [medicamentos, setMedicamentos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [statusValidade, setStatusValidade] = useState('');
  const [viewMode, setViewMode] = useState('individual'); // 'individual' ou 'grupos'
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSearching, setIsSearching] = useState(false);

  // Aplicar debounce na pesquisa com 500ms de delay
  const debouncedSearch = useDebounce(search, 500);

  // Ler parâmetros da URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    if (statusParam === 'vencidos') {
      setStatusValidade('vencidos');
    }
  }, [location.search]);

  const categorias = [
    'analgesico',
    'antibiotico',
    'antinflamatorio',
    'cardiovascular',
    'dermatologico',
    'gastrointestinal',
    'neurologico',
    'psiquiatrico',
    'respiratorio',
    'vitaminas',
    'outros'
  ];

  const categoriasLabels = {
    'analgesico': 'Analgésico',
    'antibiotico': 'Antibiótico',
    'antinflamatorio': 'Anti-inflamatório',
    'cardiovascular': 'Cardiovascular',
    'dermatologico': 'Dermatológico',
    'gastrointestinal': 'Gastrointestinal',
    'neurologico': 'Neurológico',
    'psiquiatrico': 'Psiquiátrico',
    'respiratorio': 'Respiratório',
    'vitaminas': 'Vitaminas',
    'outros': 'Outros'
  };

  const carregarMedicamentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: debouncedSearch,
        categoria
      };
      
      const response = await medicamentoService.getAll(params);
      let medicamentosFiltrados = response.medicamentos || [];
      
      // Aplicar filtro de status de validade no frontend
      if (statusValidade) {
        const hoje = new Date();
        medicamentosFiltrados = medicamentosFiltrados.filter(med => {
          const dataValidade = new Date(med.dataValidade);
          const diasParaVencer = Math.ceil((dataValidade - hoje) / (1000 * 60 * 60 * 24));
          
          switch (statusValidade) {
            case 'vencidos':
              return dataValidade < hoje;
            case 'vencendo':
              return diasParaVencer <= 30 && diasParaVencer > 0;
            default:
              return true;
          }
        });
      }
      
      setMedicamentos(medicamentosFiltrados);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar medicamentos:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar medicamentos' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, categoria, statusValidade]);

  const carregarGrupos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        search: debouncedSearch,
        categoria
      };
      
      const response = await medicamentoService.getGrupos(params);
      setGrupos(response.grupos || response || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar grupos de medicamentos' });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoria]);

  useEffect(() => {
    if (viewMode === 'individual') {
      carregarMedicamentos();
    } else {
      carregarGrupos();
    }
  }, [viewMode, carregarMedicamentos, carregarGrupos]);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      // Limpar a mensagem do state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value !== debouncedSearch) {
      setIsSearching(true);
    }
  };

  // Resetar página quando o debouncedSearch muda
  useEffect(() => {
    setCurrentPage(1);
    setIsSearching(false);
  }, [debouncedSearch]);

  const handleCategoriaChange = (e) => {
    setCategoria(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusValidadeChange = (e) => {
    setStatusValidade(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (medicamento) => {
    navigate(`/medicamentos/editar/${medicamento.id}`);
  };

  const handleDelete = async (medicamento) => {
    if (window.confirm(`Tem certeza que deseja excluir o medicamento "${medicamento.nome}"?`)) {
      try {
        await medicamentoService.delete(medicamento.id);
        setMessage({ type: 'success', text: 'Medicamento excluído com sucesso!' });
        if (viewMode === 'individual') {
          carregarMedicamentos();
        } else {
          carregarGrupos();
        }
      } catch (error) {
        console.error('Erro ao excluir medicamento:', error);
        setMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Erro ao excluir medicamento'
        });
      }
    }
  };

  const handleViewGrupoDetalhes = (grupo) => {
    // Usar o ID do medicamento registrado se disponível
    if (grupo.id) {
      navigate(`/medicamentos/grupos/detalhes?id=${grupo.id}`);
    } else {
      // Fallback para API antiga
      const params = new URLSearchParams({
        nome: grupo.nome,
        principioAtivo: grupo.principioAtivo,
        fabricante: grupo.fabricante,
        tipoControle: grupo.tipoControle,
        prescricaoObrigatoria: grupo.prescricaoObrigatoria
      });
      
      if (grupo.codigoBarras) {
        params.append('codigoBarras', grupo.codigoBarras);
      }
      
      if (grupo.dosagem) {
        params.append('dosagem', grupo.dosagem);
      }
      
      if (grupo.formaFarmaceutica) {
        params.append('formaFarmaceutica', grupo.formaFarmaceutica);
      }
      
      if (grupo.categoria) {
        params.append('categoria', grupo.categoria);
      }
      
      navigate(`/medicamentos/grupos/detalhes?${params.toString()}`);
    }
  };

  // Funções auxiliares
  const getStatusEstoque = (estoque, estoqueMinimo) => {
    if (estoque === 0) return { color: '#dc3545', status: 'Esgotado' };
    if (estoque <= estoqueMinimo) return { color: '#f57c00', status: 'Baixo' };
    return { color: '#28a745', status: 'Normal' };
  };

  const isVencido = (dataValidade) => {
    return new Date(dataValidade) < new Date();
  };

  const isVencendoProximo = (dataValidade) => {
    const hoje = new Date();
    const dataVal = new Date(dataValidade);
    const diasParaVencer = Math.ceil((dataVal - hoje) / (1000 * 60 * 60 * 24));
    return diasParaVencer <= 30 && diasParaVencer > 0;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getCorTipoControle = (tipo) => {
    switch (tipo) {
      case 'normal':
        return '#28a745';
      case 'especial':
        return '#ffc107';
      case 'sem_controle':
        return '#6c757d';
      default:
        return '#007bff';
    }
  };

  const formatarTipoControle = (tipo) => {
    switch (tipo) {
      case 'normal':
        return 'Normal';
      case 'especial':
        return 'Especial';
      case 'sem_controle':
        return 'Sem Controle';
      default:
        return tipo;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div>Carregando medicamentos...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div className="header-section" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Medicamentos</h2>
          
          {user.cargo !== 'visitante' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className={`btn ${viewMode === 'individual' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setViewMode('individual')}
              style={{ padding: '8px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <List size={16} />
              Individual
            </button>
            <button
              className={`btn ${viewMode === 'grupos' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setViewMode('grupos')}
              style={{ padding: '8px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <Layers size={16} />
              Grupos
            </button>
          </div>
          )}
        
          {(user.cargo === 'admin' || user.cargo === 'farmaceutico') && (
            <Link to="/medicamentos/novo" className="btn btn-primary">
              <Plus size={20} />
              Novo Medicamento
            </Link>
          )}
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      <div className="card">
        {/* Filtros */}
        <div className="search-box">
          <div className="search-input" style={{ position: 'relative' }}>
            <Search size={20} style={{ 
              position: 'absolute', 
              left: '10px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: isSearching ? '#007bff' : '#667'
            }} />
            {isSearching && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#007bff',
                fontSize: '12px'
              }}>
                Buscando...
              </div>
            )}
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nome, princípio ativo, fabricante ou código de barras..."
              value={search}
              onChange={handleSearch}
              style={{ 
                paddingLeft: '40px',
                paddingRight: isSearching ? '80px' : '10px'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Filter size={20} color="#667" />
              <select
                className="form-control"
                value={categoria}
                onChange={handleCategoriaChange}
                style={{ minWidth: '150px' }}
              >
                <option value="">Todas as categorias</option>
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{categoriasLabels[cat]}</option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Calendar size={20} color="#667" />
              <select
                className="form-control"
                value={statusValidade}
                onChange={handleStatusValidadeChange}
                style={{ minWidth: '170px' }}
              >
                <option value="">Todos</option>
                <option value="vencidos">Vencidos</option>
                <option value="vencendo">Vencendo em 30 dias</option>
              </select>
            </div>
          </div>
        </div>

        {/* Renderização Condicional */}
        {viewMode === 'individual' ? (
          // Visualização Individual
          medicamentos.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Princípio Ativo</th>
                      <th>Fabricante</th>
                      <th>Categoria</th>
                      <th>Tipo de Controle</th>
                      <th>Estoque</th>
                      <th>Validade</th>
                      {user.cargo !== 'visitante' && <th>Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {medicamentos.map((medicamento) => {
                      const statusEstoque = getStatusEstoque(medicamento.estoque, medicamento.estoqueMinimo);
                      const vencendoProximo = isVencendoProximo(medicamento.dataValidade);
                      const vencido = isVencido(medicamento.dataValidade);
                      
                      // Obter dados do medicamento registrado ou usar campos diretos
                      const dadosMedicamento = medicamento.medicamentoRegistrado || medicamento;
                      
                      return (
                        <tr 
                          key={medicamento.id}
                          style={{
                            backgroundColor: vencido ? '#fff5f5' : vencendoProximo ? '#fffbeb' : 'transparent',
                            borderLeft: vencido ? '4px solid #dc3545' : vencendoProximo ? '4px solid #f59e0b' : '4px solid transparent'
                          }}
                        >
                          <td>
                            <div>
                              <strong>{dadosMedicamento.nome}</strong>
                              {dadosMedicamento.dosagem && (
                                <div style={{ fontSize: '12px', color: '#667' }}>
                                  {dadosMedicamento.dosagem}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{dadosMedicamento.principioAtivo || dadosMedicamento.principio_ativo}</td>
                          <td>{medicamento.fabricante}</td>
                          <td>
                            {dadosMedicamento.categoria && (
                              <span style={{
                                background: '#e9ecef',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {categoriasLabels[dadosMedicamento.categoria] || dadosMedicamento.categoria}
                              </span>
                            )}
                          </td>
                          <td>
                            <span style={{
                              background: getCorTipoControle(dadosMedicamento.tipoControle || dadosMedicamento.tipo_controle),
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {formatarTipoControle(dadosMedicamento.tipoControle || dadosMedicamento.tipo_controle)}
                            </span>
                          </td>
                          <td>
                            <span style={{ 
                              color: statusEstoque.color,
                              fontWeight: 'bold'
                            }}>
                              {medicamento.estoque}
                            </span>
                            {medicamento.estoque <= medicamento.estoqueMinimo && (
                              <AlertTriangle size={16} color={statusEstoque.color} style={{ marginLeft: '5px' }} />
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{
                                color: vencido ? '#dc3545' : vencendoProximo ? '#f57c00' : '#333',
                                fontWeight: vencido || vencendoProximo ? 'bold' : 'normal'
                              }}>
                                {formatDate(medicamento.dataValidade)}
                              </span>
                              <span style={{
                                fontSize: '11px',
                                color: vencido ? '#dc3545' : vencendoProximo ? '#f57c00' : '#667',
                                fontWeight: vencido || vencendoProximo ? 'bold' : 'normal'
                              }}>
                                Lote: {medicamento.lote}
                              </span>
                              {medicamento.numeroNotaFiscal && (
                                <span style={{
                                  fontSize: '11px',
                                  color: '#667',
                                  marginTop: '2px'
                                }}>
                                  NF: {medicamento.numeroNotaFiscal}
                                </span>
                              )}
                            </div>
                          </td>
                          {user.cargo !== 'visitante' && (
                          <td>
                            <div className="actions-cell">
                              <button
                                onClick={() => handleEdit(medicamento)}
                                className="btn btn-sm btn-outline-primary"
                                title="Editar"
                                disabled={user.cargo === 'atendente'}
                              >
                                <Edit2 size={16} />
                              </button>
                              
                              {user.cargo === 'admin' && (
                                <button
                                  onClick={() => handleDelete(medicamento)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Excluir"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="pagination" style={{ 
                marginTop: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <span style={{ margin: '0 20px', color: '#667' }}>
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: '#667'
            }}>
              <Package size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
              <h3>Nenhum medicamento encontrado</h3>
              <p>
                {search || categoria 
                  ? 'Tente ajustar os filtros de busca' 
                  : 'Comece cadastrando o primeiro medicamento'
                }
              </p>
              {(user.cargo === 'admin' || user.cargo === 'farmaceutico') && !search && !categoria && (
                <Link to="/medicamentos/novo" className="btn btn-primary" style={{ marginTop: '15px' }}>
                  <Plus size={20} />
                  Cadastrar Primeiro Medicamento
                </Link>
              )}
            </div>
          )
        ) : (
          // Visualização de Grupos
          grupos.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px', padding: '20px' }}>
              {grupos.map((grupo, index) => {
                const temProblemasValidade = grupo.medicamentosVencidos > 0 || grupo.medicamentosVencendo > 0;
                const estoqueTotal = parseInt(grupo.estoqueTotal);
                const estoqueMinimo = parseInt(grupo.estoqueMinimo);
                const estoqueProblema = estoqueTotal <= estoqueMinimo;
                
                return (
                  <div 
                    key={index} 
                    className="card grupo-medicamento"
                    style={{
                      cursor: 'pointer',
                      padding: '20px',
                      borderRadius: '10px',
                      border: '1px solid #e0e0e0',
                      borderLeft: temProblemasValidade ? '4px solid #dc3545' : estoqueProblema ? '4px solid #f59e0b' : '4px solid #28a745',
                      backgroundColor: temProblemasValidade ? '#fff5f5' : estoqueProblema ? '#fffbeb' : 'white',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onClick={() => handleViewGrupoDetalhes(grupo)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                          {grupo.nome}
                        </h4>
                        {grupo.dosagem && (
                          <p style={{ margin: '0', fontSize: '14px', color: '#667' }}>
                            {grupo.dosagem}
                          </p>
                        )}
                      </div>
                      <span style={{
                        background: '#007bff',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {grupo.quantidade} lote{grupo.quantidade > 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ fontSize: '14px', color: '#667', marginBottom: '8px' }}>
                        <strong>Princípio Ativo:</strong> {grupo.principioAtivo || grupo.principio_ativo || 'Não informado'}
                      </div>
                      {grupo.categoria && (
                        <div style={{ fontSize: '14px', color: '#667', marginBottom: '8px' }}>
                          <strong>Categoria:</strong> 
                          <span style={{
                            background: '#e9ecef',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            marginLeft: '8px'
                          }}>
                            {grupo.categoria}
                          </span>
                        </div>
                      )}
                      <div style={{ fontSize: '14px', color: '#667' }}>
                        <strong>Controle:</strong>
                        <span style={{
                          background: getCorTipoControle(grupo.tipoControle || grupo.tipo_controle),
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          marginLeft: '8px'
                        }}>
                          {formatarTipoControle(grupo.tipoControle || grupo.tipo_controle)}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <div>
                        <div style={{ fontSize: '14px', color: '#667' }}>Estoque Total</div>
                        <div style={{ 
                          fontSize: '20px', 
                          fontWeight: 'bold',
                          color: estoqueProblema ? '#f59e0b' : '#28a745',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}>
                          {estoqueTotal}
                          {estoqueProblema && <AlertTriangle size={16} />}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '14px', color: '#667' }}>Estoque Mínimo</div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                          {estoqueMinimo}
                        </div>
                      </div>
                    </div>
                    
                    {temProblemasValidade && (
                      <div style={{ 
                        background: '#fff2f2', 
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        padding: '10px',
                        marginBottom: '15px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                          <AlertTriangle size={16} color="#dc3545" />
                          <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc3545' }}>
                            Atenção - Problemas de Validade
                          </span>
                        </div>
                        {grupo.medicamentosVencidos > 0 && (
                          <div style={{ fontSize: '12px', color: '#dc3545' }}>
                            • {grupo.medicamentosVencidos} medicamento{grupo.medicamentosVencidos > 1 ? 's' : ''} vencido{grupo.medicamentosVencidos > 1 ? 's' : ''}
                          </div>
                        )}
                        {grupo.medicamentosVencendo > 0 && (
                          <div style={{ fontSize: '12px', color: '#dc3545' }}>
                            • {grupo.medicamentosVencendo} medicamento{grupo.medicamentosVencendo > 1 ? 's' : ''} vencendo em 30 dias
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'flex-end',
                      paddingTop: '15px',
                      borderTop: '1px solid #e9ecef'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#007bff' }}>
                        <Eye size={14} />
                        Ver Detalhes
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: '#667'
            }}>
              <Layers size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
              <h3>Nenhum grupo de medicamento encontrado</h3>
              <p>
                {search || categoria 
                  ? 'Tente ajustar os filtros de busca' 
                  : 'Comece cadastrando medicamentos para formar grupos'
                }
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Medicamentos;